import { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { notifications } from "@mantine/notifications";
import {
    Paper,
    TextInput,
    ActionIcon,
    Stack,
    Text,
    ScrollArea,
    Group,
    Loader,
    Box,
    Affix,
    Transition,
    ThemeIcon,
    useMantineColorScheme,
    Button,
} from "@mantine/core";
import { ReportIcon, BarChartIcon, LineChartIcon, BarLineChartIcon, RadarChartIcon, StackedLineChartIcon, FormIcon, PieChartIcon, DashboardIcon, GaugeChartIcon, TabGroupIcon, ScoreCardIcon, AreaChartIcon, CalendarChartIcon, MapChartIcon } from "@bi-studio/shared/components";
import { Send, X, ChevronRight, Sparkles, FileText, Database, CheckCircle2 } from "lucide-react";
import { useAuth } from "@bi-studio/shared/hooks";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { ChatMessage, RelatedItem, CreateReportContext } from "../../types/ai";
import { createReport, executeReport, updateReport } from "@bi-studio/shared/apis";

export function AiAssistant() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingReport, setIsCreatingReport] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const { colorScheme } = useMantineColorScheme();

    // Auto-scroll to bottom
    const viewport = useRef<HTMLDivElement>(null);
    const scrollToBottom = () => {
        if (viewport.current) {
            viewport.current.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, statusMessage, isOpen]);

    useEffect(() => {
        if (!token) return;

        const socketUrl = import.meta.env.VITE_API_URL || "http://localhost:8080";
        const newSocket = io(socketUrl, {
            auth: { token },
            transports: ["websocket"],
        });

        newSocket.on("connect", () => {
            console.log("Connected to AI Assistant socket");
        });

        newSocket.on("status_update", (data: { status: string; message: string }) => {
            setStatusMessage(data.message);
        });

        newSocket.on("chat_response", (data: { text: string; relatedItems?: RelatedItem[]; createReportContext?: CreateReportContext }) => {
            setIsLoading(false);
            setStatusMessage(null);
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now().toString(),
                    role: "assistant",
                    content: data.text,
                    relatedItems: data.relatedItems,
                    createReportContext: data.createReportContext,
                    timestamp: Date.now(),
                },
            ]);
        });

        newSocket.on("error", (data: { message: string }) => {
            setIsLoading(false);
            setStatusMessage(null);
            notifications.show({
                title: 'AI Assistant Error',
                message: data.message,
                color: 'red',
            });
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now().toString(),
                    role: "assistant",
                    content: `Error: ${data.message}`,
                    timestamp: Date.now(),
                },
            ]);
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [token]);

    const handleSend = () => {
        if (!input.trim() || !socket) return;

        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            role: "user",
            content: input,
            timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setIsLoading(true);
        setStatusMessage("Thinking...");

        socket.emit("chat_request", {
            prompt: input,
        });
    };

    const handleNavigate = (link: string) => {
        navigate(link);
        // Optional: Close chat on navigation?
        // setIsOpen(false);
    };

    const handleCreateReport = async (ctx: CreateReportContext) => {
        if (!token || !ctx.name || !ctx.dataSourceId || !ctx.query) return;

        try {
            setIsCreatingReport(true);

            // Step 1: Create the report
            const report = await createReport(token, {
                name: ctx.name,
                dataSourceId: ctx.dataSourceId,
                query: ctx.query,
                description: ctx.description,
            });

            // Step 2: Run the report behind the scenes to auto-detect and configure columns
            let columnsDetected = false;
            try {
                const result = await executeReport(token, report.id, {
                    pagination: { page: 1, pageSize: 1 },
                });

                if (result.columns && result.columns.length > 0) {
                    await updateReport(token, report.id, { columns: result.columns });
                    columnsDetected = true;
                }
            } catch (_execErr) {
                // Execution failed — report still created, user can fix query in edit page
                console.warn("Could not auto-configure columns for AI-created report:", _execErr);
            }

            notifications.show({
                title: "Report Created",
                message: `"${report.name}" has been created successfully.`,
                color: "green",
            });

            // Add a success message with navigation link
            const successLink = columnsDetected ? `/reports/${report.id}` : `/reports/${report.id}/edit`;
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now().toString(),
                    role: "assistant",
                    content: columnsDetected
                        ? `✅ Report **"${report.name}"** has been created and is ready to view!`
                        : `✅ Report **"${report.name}"** has been created. Click below to open the edit page and preview it.`,
                    relatedItems: [
                        {
                            type: "report" as const,
                            id: report.id,
                            name: report.name,
                            link: successLink,
                        },
                    ],
                    timestamp: Date.now(),
                },
            ]);
        } catch (err: any) {
            notifications.show({
                title: "Failed to create report",
                message: err.message || "An error occurred",
                color: "red",
            });
        } finally {
            setIsCreatingReport(false);
        }
    };

    return (
        <Affix position={{ bottom: 20, right: 20 }}>
            <Transition transition="slide-up" mounted={isOpen}>
                {(transitionStyles) => (
                    <Paper
                        withBorder
                        shadow="xl"
                        style={{
                            ...transitionStyles,
                            position: 'absolute',
                            bottom: 60,
                            right: 0,
                            width: 380,
                            height: 500,
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 1000,
                            overflow: 'hidden',
                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : undefined,
                            borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined,
                            backdropFilter: colorScheme === 'dark' ? 'blur(10px)' : undefined
                        }}
                        radius="md"
                    >
                        <Box
                            p="sm"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: colorScheme === 'dark'
                                    ? 'rgba(255, 255, 255, 0.03)'
                                    : 'linear-gradient(135deg, var(--mantine-color-black-7) 0%, var(--mantine-color-black-9) 100%)',
                                color: colorScheme === 'dark' ? 'var(--mantine-color-text)' : 'var(--mantine-color-white)',
                                borderBottom: colorScheme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                            }}
                        >
                            <Group gap="xs">
                                <Sparkles size={20} />
                                <Text fw={600}>AI Assistant</Text>
                            </Group>
                            <ActionIcon variant="transparent" c="white" onClick={() => setIsOpen(false)}>
                                <X size={18} />
                            </ActionIcon>
                        </Box>

                        <ScrollArea flex={1} p="sm" viewportRef={viewport} bg="transparent">
                            <Stack gap="sm">
                                {messages.length === 0 && (
                                    <Text c="dimmed" size="sm" ta="center" mt="xl" px="xl">
                                        Hi! I can help you find reports, dashboards, and answer questions about your data.
                                    </Text>
                                )}

                                {messages.map((msg) => (
                                    <Box
                                        key={msg.id}
                                        style={{
                                            alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                                            maxWidth: "85%",
                                        }}
                                    >
                                        <Paper
                                            p="xs"
                                            radius="md"
                                            withBorder
                                            style={{
                                                backgroundColor: msg.role === "user"
                                                    ? 'var(--mantine-color-brand-light)'
                                                    : 'var(--mantine-color-default)',
                                                borderColor: msg.role === "user"
                                                    ? 'var(--mantine-color-brand-light-color)'
                                                    : 'var(--mantine-color-default-border)',
                                                color: 'var(--mantine-color-text)'
                                            }}
                                        >
                                            <div style={{ fontSize: '14px' }}>
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm, remarkBreaks]}
                                                    components={{
                                                        p: ({ node, ...props }) => <p style={{ margin: 0, marginBottom: '0.5em' }} {...props} />,
                                                        a: ({ node, ...props }) => <a style={{ color: 'inherit', textDecoration: 'underline' }} {...props} />,
                                                        ul: ({ node, ...props }) => <ul style={{ margin: 0, paddingLeft: '1.2em' }} {...props} />,
                                                        ol: ({ node, ...props }) => <ol style={{ margin: 0, paddingLeft: '1.2em' }} {...props} />,
                                                        code: ({ node, ...props }) => <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '0.2em 0.4em', borderRadius: '3px', fontFamily: 'monospace' }} {...props} />,
                                                    }}
                                                >
                                                    {msg.content}
                                                </ReactMarkdown>
                                            </div>
                                        </Paper>

                                        {msg.relatedItems && msg.relatedItems.length > 0 && (
                                            <Paper
                                                mt="xs"
                                                p={0}
                                                radius="sm"
                                                withBorder
                                                style={{ overflow: 'hidden', borderColor: 'var(--mantine-color-default-border)' }}
                                            >
                                                <Box p="xs" bg="var(--mantine-color-default-hover)">
                                                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">Found {msg.relatedItems.length} items</Text>
                                                </Box>
                                                <Stack gap={0}>
                                                    {msg.relatedItems.map((item, index) => {
                                                        const isReport = item.type === 'report';
                                                        const isChart = item.type === 'chart';
                                                        const Icon = isReport || isChart ? null :
                                                            item.type === 'dashboard' ? DashboardIcon :
                                                                item.type === 'tab-group' ? TabGroupIcon : FormIcon;

                                                        const color = item.type === 'report' ? 'gray' :
                                                            item.type === 'dashboard' ? 'dark' :
                                                                item.type === 'chart' ? 'brand' : 'gray';

                                                        return (
                                                            <Box
                                                                key={`${item.type}-${item.id}`}
                                                                component="button"
                                                                onClick={() => handleNavigate(item.link || `/reports/${item.id}`)}
                                                                p="xs"
                                                                style={{
                                                                    textAlign: 'left',
                                                                    width: '100%',
                                                                    borderTop: index > 0 ? '1px solid var(--mantine-color-default-border)' : 'none',
                                                                    transition: 'background-color 0.2s'
                                                                }}
                                                                className="hover:bg-gray-50 dark:hover:bg-zinc-800"
                                                            >
                                                                <Group gap="sm" wrap="nowrap">
                                                                    <ThemeIcon variant="light" color={color} size="md" radius="md">
                                                                        {isReport ? (
                                                                            <ReportIcon size={32} />
                                                                        ) : isChart ? (
                                                                            (() => {
                                                                                if (item.chartType === 'line') return <LineChartIcon size={32} />;
                                                                                if (item.chartType === 'gauge') return <GaugeChartIcon size={32} />;
                                                                                if (item.chartType === 'pie') return <PieChartIcon size={32} />;
                                                                                if (item.chartType === 'stacked-line') return <StackedLineChartIcon size={32} />;
                                                                                if (item.chartType === 'stacked-bar-line') return <BarLineChartIcon size={32} />;
                                                                                if (item.chartType === 'radar') return <RadarChartIcon size={32} />;
                                                                                if (item.chartType === 'scorecard') return <ScoreCardIcon size={32} />;
                                                                                if (item.chartType === 'area') return <AreaChartIcon size={32} />;
                                                                                if (item.chartType === 'calendar') return <CalendarChartIcon size={32} />;
                                                                                if (item.chartType === 'map') return <MapChartIcon size={32} />;
                                                                                return <BarChartIcon size={32} />;
                                                                            })()
                                                                        ) : (
                                                                            Icon && <Icon size={16} />
                                                                        )}
                                                                    </ThemeIcon>
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        <Text size="sm" fw={500} truncate>{item.name}</Text>
                                                                        <Text size="xs" c="dimmed" tt="capitalize">{item.type}</Text>
                                                                    </div>
                                                                    <ActionIcon variant="transparent" color="gray" size="sm">
                                                                        <ChevronRight size={14} />
                                                                    </ActionIcon>
                                                                </Group>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            </Paper>
                                        )}

                                        {/* Create Report Context UI */}
                                        {msg.createReportContext && msg.role === "assistant" && (
                                            <Paper
                                                mt="xs"
                                                p="sm"
                                                radius="sm"
                                                withBorder
                                                style={{
                                                    borderColor: msg.createReportContext.stage === 'ready_to_create'
                                                        ? 'var(--mantine-color-green-6)'
                                                        : 'var(--mantine-color-brand-6)',
                                                    background: colorScheme === 'dark'
                                                        ? msg.createReportContext.stage === 'ready_to_create'
                                                            ? 'rgba(34, 139, 34, 0.08)'
                                                            : 'rgba(255, 255, 255, 0.02)'
                                                        : msg.createReportContext.stage === 'ready_to_create'
                                                            ? 'rgba(34, 139, 34, 0.05)'
                                                            : 'rgba(var(--mantine-color-brand-2-rgb), 0.2)',
                                                }}
                                            >
                                                <Stack gap="xs">
                                                    <Group gap="xs">
                                                        <ThemeIcon
                                                            size="sm"
                                                            variant="light"
                                                            color={msg.createReportContext.stage === 'ready_to_create' ? 'green' : 'brand'}
                                                            radius="xl"
                                                        >
                                                            {msg.createReportContext.stage === 'ready_to_create'
                                                                ? <CheckCircle2 size={12} />
                                                                : <FileText size={12} />}
                                                        </ThemeIcon>
                                                        <Text size="xs" fw={700} c={msg.createReportContext.stage === 'ready_to_create' ? 'green' : 'brand'} tt="uppercase">
                                                            {msg.createReportContext.stage === 'ready_to_create' ? 'Ready to Create' : 'Gathering Details'}
                                                        </Text>
                                                    </Group>

                                                    {/* Show gathered details */}
                                                    <Stack gap={4}>
                                                        {msg.createReportContext.name && (
                                                            <Group gap="xs">
                                                                <FileText size={12} opacity={0.6} />
                                                                <Text size="xs" c="dimmed">Name: <Text span size="xs" fw={500} c="var(--mantine-color-text)">{msg.createReportContext.name}</Text></Text>
                                                            </Group>
                                                        )}
                                                        {msg.createReportContext.dataSourceName && (
                                                            <Group gap="xs">
                                                                <Database size={12} opacity={0.6} />
                                                                <Text size="xs" c="dimmed">Data Source: <Text span size="xs" fw={500} c="var(--mantine-color-text)">{msg.createReportContext.dataSourceName}</Text></Text>
                                                            </Group>
                                                        )}
                                                    </Stack>

                                                    {/* Create button only when ready */}
                                                    {msg.createReportContext.stage === 'ready_to_create' && (
                                                        <Button
                                                            size="xs"
                                                            color="green"
                                                            variant="filled"
                                                            leftSection={<CheckCircle2 size={13} />}
                                                            loading={isCreatingReport}
                                                            onClick={() => handleCreateReport(msg.createReportContext!)}
                                                            fullWidth
                                                        >
                                                            Create Report
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        )}

                                        <Text size="xs" c="dimmed" mt={2} ta={msg.role === "user" ? "right" : "left"}>
                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Text>
                                    </Box>
                                ))}

                                {isLoading && (
                                    <Group gap="xs">
                                        <Loader size="xs" variant="dots" />
                                        <Text size="xs" c="dimmed">{statusMessage || "Thinking..."}</Text>
                                    </Group>
                                )}
                            </Stack>
                        </ScrollArea>

                        <Box p="sm" style={{ borderTop: colorScheme === 'dark' ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid var(--mantine-color-default-border)", backgroundColor: 'transparent' }}>
                            <TextInput
                                placeholder="Ask something..."
                                value={input}
                                onChange={(e) => setInput(e.currentTarget.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                styles={{
                                    input: {
                                        backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : undefined,
                                        borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : undefined,
                                        color: 'var(--mantine-color-text)',
                                    }
                                }}
                                rightSection={
                                    <ActionIcon
                                        onClick={handleSend}
                                        variant="filled"
                                        color="black"
                                        size="sm"
                                        disabled={!input.trim() || isLoading}
                                        style={{
                                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined,
                                            color: colorScheme === 'dark' ? 'white' : undefined
                                        }}
                                    >
                                        <Send size={14} />
                                    </ActionIcon>
                                }
                            />
                        </Box>
                    </Paper>
                )}
            </Transition>

            <ActionIcon
                variant="filled"
                color="black"
                size={50}
                radius="xl"
                onClick={() => setIsOpen(!isOpen)}
                style={{ zIndex: 999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
            >
                {isOpen ? <X size={24} /> : <Sparkles size={24} />}
            </ActionIcon>
        </Affix>
    );
}
